import { useListener as useListener_ } from '#~/command/hooks.js';
import { Command } from '#~/command/index.js';
import { Slicer, useDepot } from '#~/depot/hooks.js';
import type { Device } from '#~/device/index.js';
import { isVideoDevice, toStatus } from '#~/device/index.js';
import { useDeviceIds, useRoom, useSelectedVideo } from '#~/room/hooks.js';
import { usePopupStore } from '#~/store/popup.js';
import type { Props as SettingsProps } from '#~/video/components/settings/index.js';
import { Settings } from '#~/video/components/settings/index.js';
import { useAddVideoStore } from './add.js';
import type { Api } from '@studio/design-system/dialog';
import { useDialog } from '@studio/design-system/dialog';
import type { Room } from '@studio/models/room';
import type { EventHandler } from '@studio/react-utils/event';
import { useComposedRefs, useLatestFn, useLazyRef } from '@studio/react-utils/hooks';
import { partial } from 'lodash-es';
import type { ReactElement } from 'react';
import { createElement, forwardRef, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';

// @internal
const SettingsWrapper = forwardRef<
  Api,
  Omit<SettingsProps, 'agentId' | 'status'> & { roomId: Room['id'] }
>(({ device, roomId, onOpenChange, ...props }, forwardedRef): ReactElement => {
  const [activeDevice, setActiveDevice] = useState(device);
  const apiRef = useRef<Api>(null);
  const isOpen = useRef(props.defaultOpen ?? true);
  const handleOpenChange = useLatestFn((open: boolean) => {
    // Record close synchronously: the dialog API's getter updates on render,
    // so a commit resolving in this same tick must not reopen the editor.
    isOpen.current = open;
    onOpenChange?.(open);
  });
  const handleDeviceCommitted = useLatestFn((committed: Device) => {
    if (isOpen.current && apiRef.current) setActiveDevice(committed);
  });
  const passthru = useDepot((µ) => {
    const room = new Slicer(µ).room(roomId);

    return {
      agentId: activeDevice.agentId,
      status: toStatus(room.devices[activeDevice.id])
    };
  }, shallow);

  // Forward `roomId` into <Settings> (Multi-Add Phase 2): the settings store
  // needs it so the Authentication tab can resolve the room for a transitory
  // Iris placeholder (whose `agent` is not joined yet, so `agent.roomId` is
  // undefined and the auth-dispatch guard would reject Submit with "No room
  // available to authenticate against."). Previously roomId was consumed only
  // for the status lookup above and dropped here.
  // Per `reference-rules-in-comments` (ai.knowledge/global).
  // An explicit placeholder-to-camera transition changes the Provider key and
  // resets its draft/snapshot together. Ordinary status updates keep the draft.
  return createElement(Settings, {
    ...props,
    ...passthru,
    device: activeDevice,
    roomId,
    onDeviceCommitted: handleDeviceCommitted,
    onOpenChange: handleOpenChange,
    ref: useComposedRefs(apiRef, forwardedRef)
  });
});

/**
 * Hook that returns a function to imperatively start the 'add video' workflow.
 *
 * @returns Imperative function.
 */
export const useAddVideo = (): (() => void) => {
  const { setShow } = useAddVideoStore();
  const { hidePopup } = usePopupStore();

  return () => {
    hidePopup();
    setShow(true);
  };
};

/** Command listener. */
export const useListener = (): void => {
  const [selectedId, setSelectedId] = useSelectedVideo();
  const { videoIds } = useDeviceIds();

  const handleSelectByIndex = useLatestFn((index: number) => {
    const nextId = videoIds[index];

    if (nextId) {
      setSelectedId(nextId);
    }
  });

  const handleSelectOffset = useLatestFn((offset: -1 | 1) => {
    if (videoIds.length === 0) {
      return;
    }

    const currentIndex = selectedId ? videoIds.indexOf(selectedId) : -1;

    handleSelectByIndex((Math.max(currentIndex, 0) + offset + videoIds.length) % videoIds.length);
  });

  const jumpTable = useLazyRef<{ [key in Command]?: EventHandler }>(() => ({
    [Command.VIDEO_SOURCE_01]: partial(handleSelectByIndex, 0),
    [Command.VIDEO_SOURCE_02]: partial(handleSelectByIndex, 1),
    [Command.VIDEO_SOURCE_03]: partial(handleSelectByIndex, 2),
    [Command.VIDEO_SOURCE_04]: partial(handleSelectByIndex, 3),
    [Command.VIDEO_SOURCE_05]: partial(handleSelectByIndex, 4),
    [Command.VIDEO_SOURCE_06]: partial(handleSelectByIndex, 5),
    [Command.VIDEO_SOURCE_07]: partial(handleSelectByIndex, 6),
    [Command.VIDEO_SOURCE_08]: partial(handleSelectByIndex, 7),
    [Command.VIDEO_SOURCE_09]: partial(handleSelectByIndex, 8),
    [Command.VIDEO_SOURCE_10]: partial(handleSelectByIndex, 9)
  })).current;

  useListener_(({ command }) => {
    if (videoIds.length === 0) {
      return;
    }

    if (command === Command.VIDEO_SOURCE_NEXT) {
      handleSelectOffset(1);
    } else if (command === Command.VIDEO_SOURCE_PREV) {
      handleSelectOffset(-1);
    } else {
      jumpTable[command]?.();
    }
  });
};

/**
 * Hook that returns a function to imperatively open the video 'advanced settings' panel.
 * @param roomId - The room id.
 *
 * @returns Imperative function.
 *
 * The returned function accepts an optional `options` argument
 * carrying `defaultPanel` + `defaultTab` deep-link targets. The chip
 * click in `item.tsx` passes `{ defaultPanel: Panel.IrisConnection,
 * defaultTab: chipState.targetTab }` here so the settings dialog opens
 * at the correct sub-tab.
 *
 * `options.roomId` is a PER-CALL override of the hook-level room, for hosts
 * that are mounted ABOVE the room provider and therefore cannot know the room
 * at hook time. `<Notifications>` is the case in point: `routes/router.tsx`
 * renders it as a SIBLING of the routed `<Outlet>`, so it never sees the room
 * provider — on room routes included — and can only hand this hook an inert
 * placeholder id. Left unoverridden that placeholder reaches
 * `<SettingsWrapper>` above, where `Slicer.room()` `ensureNotNil`-throws
 * (`depot/slicer.ts`), and it would also be forwarded into authentication
 * submission. Such hosts resolve the real room when the user CLICKS and pass
 * it here; every caller rendered inside a room ignores this and keeps the
 * hook-level (or `useRoom`) value.
 * Per `reference-rules-in-comments` (ai.knowledge/global).
 */
export const useSettings = (
  roomId?: Room['id']
): ((
  device: Device,
  options?: {
    defaultPanel?: SettingsProps['defaultPanel'];
    defaultTab?: SettingsProps['defaultTab'];
    roomId?: Room['id'];
  }
) => void) => {
  const dialog = useDialog(SettingsWrapper);
  // eslint-disable-next-line
  const safeRoomId = roomId ?? useRoom((µ) => µ.id);

  return useLatestFn(
    (
      device: Device,
      options?: {
        defaultPanel?: SettingsProps['defaultPanel'];
        defaultTab?: SettingsProps['defaultTab'];
        roomId?: Room['id'];
      }
    ) => {
      if (isVideoDevice(device)) {
        // Conditionally include the deep-link options so undefined
        // values are not passed to optional props (tsconfig has
        // `exactOptionalPropertyTypes: true`). When `options` is
        // omitted, this is byte-identical to the previous call.
        const deepLinkProps = {
          ...(options?.defaultPanel !== undefined && { defaultPanel: options.defaultPanel }),
          ...(options?.defaultTab !== undefined && { defaultTab: options.defaultTab })
        };
        // A per-call room WINS over the hook-level one (see the JSDoc): the
        // dialog must always open against a room that exists in the depot,
        // otherwise `SettingsWrapper`'s `Slicer.room()` throws.
        dialog.open({ device, roomId: options?.roomId ?? safeRoomId, ...deepLinkProps });
      }
    }
  );
};
